// One way to bring pictures in (requested 2026-10-22: "this upload feature looks all over the place, make it simple").
// Loose pictures, a whole folder, or zips of them (customer feedback, Oliver: the ctrlX CORE hands its captures over as
// a zip) all become one list of pictures for one folder, which the Upload dialog shows and the ordinary
// POST /projects/{id}/images receives in batches. Zips are opened in the browser (lib/zip.ts). Pure apart from reading
// zips, so it can be tested.
import { BROWSER_UNDISPLAYABLE, WEB_IMAGE_SUFFIXES, stem } from "./dashboard";
import { extract, readZip, unreadable, type ZipEntry } from "./zip";

/** Something that may be a picture: its path inside what it came in ("cam1/a.jpg"), and how to get its bytes. */
export interface Item { path: string; open: () => Promise<Blob>; why?: string | null }
/** What the person chose: loose pictures, one folder (from the picker or dropped), or one zip. */
export interface Source { kind: "files" | "folder" | "zip"; name: string; items: Item[]; file?: File; bundle?: boolean }
/** A picture to upload and the name it gets in the project. */
export interface Pic { item: Item; name: string }
export interface Plan {
  pictures: Pic[];
  /** The folder the dialog suggests: the zip's or folder's name when that is all there is, otherwise none (""). */
  folder: string;
  /** What it came from, in words: "“captures.zip”", "the folder “line3”", "3 pictures". */
  from: string;
  /** TIFF/BMP pictures left out: a browser cannot show them. */
  skippedUndisplayable: number;
  /** A captures download's "_annotated" pictures, left out: the device drew its boxes into their pixels. */
  skippedAnnotated: number;
  /** Pictures in a zip that cannot be unpacked here, and why. */
  unreadable: { path: string; reason: string }[];
  /** The zip, when the upload is one captures download from a deployed detector: its detections can come too. */
  bundle: File | null;
}

const ext = (name: string) => { const i = name.lastIndexOf("."); return i < 0 ? "" : name.slice(i).toLowerCase(); };
const MIME: Record<string, string> = { ".jpg": "image/jpeg", ".jpeg": "image/jpeg", ".png": "image/png", ".webp": "image/webp" };
export const mimeFor = (name: string) => MIME[ext(name)] ?? "";
export const isZip = (name: string) => ext(name) === ".zip";
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** What zip tools and file managers add that is not content: macOS resource forks, hidden files, Windows thumbnail caches. */
function isJunk(path: string): boolean {
  const parts = path.split("/");
  const base = parts[parts.length - 1];
  return parts.includes("__MACOSX") || base.startsWith(".") || base.toLowerCase() === "thumbs.db";
}

/** A folder or file name the server keeps: no control characters, no stray slashes. */
const clean = (s: string) => s.replace(/[\u0000-\u001f]/g, "").trim();
/** A zip's or folder's name as a folder name: "Line 3 captures.zip" → "Line 3 captures". */
export const rootName = (name: string) => clean(isZip(name) ? stem(name) : name).replace(/\//g, "-") || "upload";

/**
 * Loose files and folders, from the picker or a drop, without the zips (see fromZip). `path` is the file's place in
 * what was chosen: its name for a loose picture, "line3/cam1/a.jpg" for one in a folder. Each top folder is a source.
 */
export function fromFiles(files: { file: File; path: string }[]): Source[] {
  const loose: Item[] = [];
  const folders = new Map<string, Item[]>();
  for (const { file, path } of files) {
    if (isZip(file.name)) continue;
    const p = path.replace(/\\/g, "/").replace(/^\/+/, "");
    const item: Item = { path: p, open: async () => file };
    const slash = p.indexOf("/");
    if (slash < 0) loose.push(item);
    else folders.set(p.slice(0, slash), [...(folders.get(p.slice(0, slash)) ?? []), { ...item, path: p.slice(slash + 1) }]);
  }
  return [
    ...[...folders].map(([name, items]) => ({ kind: "folder" as const, name, items })),
    ...(loose.length ? [{ kind: "files" as const, name: "", items: loose }] : []),
  ];
}
/** A picker's files with their place in what was chosen (webkitRelativePath for a folder). */
export const withPaths = (files: File[]) =>
  files.map((file) => ({ file, path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name }));

/**
 * What was dropped, with folders walked: each file with its place in what was dropped ("line3/cam1/a.jpg"). The
 * entries are taken before the first await, while the browser still lets the drop's data be read.
 */
export async function filesFromDrop(dt: { items?: DataTransferItemList; files: FileList }): Promise<{ file: File; path: string }[]> {
  const entries = [...(dt.items ?? [])].map((i) => i.webkitGetAsEntry?.()).filter((e): e is FileSystemEntry => !!e);
  if (!entries.length) return withPaths([...dt.files]);
  const out: { file: File; path: string }[] = [];
  const walk = async (e: FileSystemEntry): Promise<void> => {
    if (e.isFile) {
      const file = await new Promise<File>((ok, fail) => (e as FileSystemFileEntry).file(ok, fail));
      out.push({ file, path: e.fullPath.replace(/^\/+/, "") || file.name });
    } else if (e.isDirectory) {
      const reader = (e as FileSystemDirectoryEntry).createReader();
      for (;;) {     // readEntries hands a large folder over in parts, then an empty one
        const part = await new Promise<FileSystemEntry[]>((ok, fail) => reader.readEntries(ok, fail));
        if (!part.length) break;
        for (const child of part) await walk(child);
      }
    }
  };
  for (const e of entries) await walk(e);
  return out;
}

/**
 * A zip as a source. Throws ZipError when it cannot be opened. A zip with JSON sidecars naming a camera_id is a captures
 * download from a deployed InSiSo detector (backend/app/ml/capture_bundle.py), whose detections can come too.
 */
export async function fromZip(zip: File): Promise<Source> {
  const entries = await readZip(zip);
  const items = entries.map((e): Item => ({ path: e.path, why: unreadable(e), open: () => extract(zip, e, mimeFor(e.path)) }));
  return { kind: "zip", name: zip.name, items, file: zip, bundle: await isCaptureBundle(zip, entries) };
}

async function isCaptureBundle(zip: Blob, entries: ZipEntry[]): Promise<boolean> {
  const sidecars = entries.filter((e) => ext(e.path) === ".json" && !isJunk(e.path) && e.size <= 1024 * 1024 && !unreadable(e)).slice(0, 3);
  for (const e of sidecars) {
    try {
      const record = JSON.parse(await (await extract(zip, e)).text());
      if (record && typeof record === "object" && !Array.isArray(record) && "camera_id" in record) return true;
    } catch { /* not ours to judge */ }
  }
  return false;
}

/**
 * Which items are uploaded, and under which names, all into one folder.
 *
 * Subfolders are not kept as folders of their own: one upload is one set. Names are unique in a project, so two
 * pictures with the same name in different subfolders get their subfolder in front ("cam1_frame_001.jpg"), and any
 * clash left gets a number. A single folder wrapping a whole zip (what "Compress" makes of a folder) is not part of
 * any name.
 */
export function planUpload(sources: Source[]): Plan {
  const plan: Plan = { pictures: [], folder: "", from: "", skippedUndisplayable: 0, skippedAnnotated: 0, unreadable: [], bundle: null };
  const kept: { item: Item; dirs: string[]; base: string }[] = [];
  for (const s of sources) {
    const mine: { item: Item; dirs: string[]; base: string }[] = [];
    for (const item of s.items) {
      if (isJunk(item.path)) continue;
      const x = ext(item.path);
      if (BROWSER_UNDISPLAYABLE.includes(x)) { plan.skippedUndisplayable++; continue; }
      if (!WEB_IMAGE_SUFFIXES.includes(x)) continue;
      if (s.bundle && stem(item.path).endsWith("_annotated")) { plan.skippedAnnotated++; continue; }
      if (item.why) { plan.unreadable.push({ path: item.path, reason: item.why }); continue; }
      const parts = item.path.split("/").map(clean).filter((d) => d && d !== "." && d !== "..");
      mine.push({ item, dirs: parts.slice(0, -1), base: parts[parts.length - 1] ?? clean(item.path) });
    }
    const wrapped = s.kind === "zip" && mine.length > 0 && mine.every((m) => m.dirs.length > 0 && m.dirs[0] === mine[0].dirs[0]);
    kept.push(...mine.map((m) => (wrapped ? { ...m, dirs: m.dirs.slice(1) } : m)));
  }

  const count = new Map<string, number>();
  for (const k of kept) count.set(k.base, (count.get(k.base) ?? 0) + 1);
  const used = new Set<string>();
  for (const k of kept) {
    let name = (count.get(k.base) ?? 0) > 1 && k.dirs.length ? `${k.dirs.join("_")}_${k.base}` : k.base;
    for (let n = 2; used.has(name); n++) name = `${stem(k.base)}_${n}${ext(k.base)}`;
    used.add(name);
    plan.pictures.push({ item: k.item, name });
  }

  const only = sources.length === 1 ? sources[0] : null;
  plan.folder = only && only.kind !== "files" ? rootName(only.name) : "";
  plan.bundle = only?.kind === "zip" && only.bundle ? only.file ?? null : null;
  plan.from = sources.map((s) => s.kind === "zip" ? `“${s.name}”` : s.kind === "folder" ? `the folder “${s.name}”` : plural(s.items.length, "picture")).join(", ");
  return plan;
}

/**
 * The pictures whose names the project already has, and the rest. The server keeps the image it has for a name it has
 * seen (that is what makes uploading the same pictures twice harmless), so those would add nothing.
 */
export function splitTaken(pictures: Pic[], taken: ReadonlySet<string>): { fresh: Pic[]; clash: Pic[] } {
  return { fresh: pictures.filter((p) => !taken.has(p.name)), clash: pictures.filter((p) => taken.has(p.name)) };
}

/**
 * The clashing pictures under names nothing else has: `prefix` in front ("captures_frame_001.jpg"), then "_2", "_3"…
 * For new pictures that only share a name with old ones, like a camera that starts its numbering again.
 */
export function renameTaken(clash: Pic[], taken: ReadonlySet<string>, others: Pic[], prefix: string): Pic[] {
  const used = new Set([...taken, ...others.map((p) => p.name)]);
  const root = rootName(prefix);
  return clash.map((p) => {
    let name = `${root}_${p.name}`;
    for (let n = 2; used.has(name); n++) name = `${root}_${stem(p.name)}_${n}${ext(p.name)}`;
    used.add(name);
    return { ...p, name };
  });
}

/** A picture as a File under its new name, ready for the ordinary upload. */
export async function toFile(p: Pic): Promise<File> {
  return new File([await p.item.open()], p.name, { type: mimeFor(p.name) });
}
